import { useQuery } from '@tanstack/react-query'
import { useState } from 'react'
import type { DocumentOwnerKind } from '@/entities/document'
import { entitySlotsQueryOptions } from '@/entities/slot'
import { Button } from '@/shared/ui/button'
import { Dialog } from '@/shared/ui/dialog'
import { Field, Input } from '@/shared/ui/field'
import { Select } from '@/shared/ui/select'
import { toast } from '@/shared/ui/toast'
import { useDeleteDocument, useEditDocument } from '../api/edit'

interface EditDocumentDialogProps {
  documentId: string
  entityKind: DocumentOwnerKind
  entityId: string
  currentTitle: string
  currentSlotId: string
  open: boolean
  onClose: () => void
  onDeleted: () => void
}

export function EditDocumentDialog({
  documentId,
  entityKind,
  entityId,
  currentTitle,
  currentSlotId,
  open,
  onClose,
  onDeleted,
}: EditDocumentDialogProps) {
  const slotsQ = useQuery(entitySlotsQueryOptions(entityKind, entityId))
  const edit = useEditDocument(documentId)
  const del = useDeleteDocument(documentId)
  const [title, setTitle] = useState(currentTitle)
  const [slotId, setSlotId] = useState(currentSlotId)
  const [confirmDelete, setConfirmDelete] = useState(false)
  const [error, setError] = useState('')

  function submit() {
    if (!title.trim()) return setError('Укажите название')
    setError('')
    edit.mutate(
      {
        ...(title !== currentTitle ? { title } : {}),
        ...(slotId !== currentSlotId ? { slot_id: slotId } : {}),
      },
      {
        onSuccess: () => {
          toast.success('Сохранено')
          onClose()
        },
        onError: () => toast.error('Не удалось сохранить'),
      },
    )
  }

  function remove() {
    del.mutate(undefined, {
      onSuccess: () => {
        toast.success('Документ удалён')
        onDeleted()
      },
      onError: () => toast.error('Не удалось удалить'),
    })
  }

  const busy = edit.isPending || del.isPending

  return (
    <Dialog
      open={open}
      onClose={onClose}
      title="Настройки документа"
      footer={
        <>
          <Button variant="ghost" onClick={onClose} disabled={busy}>
            Отмена
          </Button>
          <Button onClick={submit} disabled={busy}>
            {edit.isPending ? 'Сохраняем…' : 'Сохранить'}
          </Button>
        </>
      }
    >
      <div className="flex flex-col gap-4">
        <Field label="Название" {...(error ? { error } : {})}>
          {(props) => <Input {...props} value={title} onChange={(e) => setTitle(e.target.value)} />}
        </Field>

        <Field label="Слот">
          {(props) => (
            <Select
              {...props}
              value={slotId}
              onChange={setSlotId}
              options={(slotsQ.data ?? []).map((s) => ({ value: s.id, label: s.name }))}
            />
          )}
        </Field>

        <div className="mt-2 flex flex-col gap-2 border-border-subtle border-t pt-4">
          {confirmDelete ? (
            <div className="flex items-center justify-between gap-3">
              <span className="text-sm text-text-secondary">
                Удалить документ со всеми версиями?
              </span>
              <div className="flex gap-2">
                <Button
                  variant="ghost"
                  size="sm"
                  onClick={() => setConfirmDelete(false)}
                  disabled={busy}
                >
                  Нет
                </Button>
                <Button variant="danger" size="sm" onClick={remove} disabled={busy}>
                  Удалить
                </Button>
              </div>
            </div>
          ) : (
            <Button
              variant="ghost"
              size="sm"
              className="self-start text-accent"
              onClick={() => setConfirmDelete(true)}
              disabled={busy}
            >
              Удалить документ
            </Button>
          )}
        </div>
      </div>
    </Dialog>
  )
}
